// Behaviour of the CoreModels value types (the Swift computed properties and static helpers), kept apart from the
// plain data shapes in models.ts.

import type {
  BivouacArea,
  BivouacTripInfo,
  Confidence,
  DataFreshness,
  DataProvenance,
  EpochMs,
  FreshnessPolicy,
  GeoCoordinate,
  JurisdictionLevel,
  LocalRuleNote,
  OfficialLayerCheck,
  OfflineContentKind,
  OfflinePackageLevel,
  OfflineRegion,
  OvernightCategory,
  OvernightPreference,
  OvernightRule,
  POICategory,
  POICategoryGroup,
  PhysicalSuitability,
  ProtectedArea,
  ProtectedAreaKind,
  Regulation,
  RegulationEffect,
  RegulatoryCoverage,
  RegulatoryState,
  SeasonalWindow,
  ThunderstormRisk,
  UserReport,
  UserReportKind,
  WeatherForecast,
  WeatherSnapshot,
} from './models';
import { boundsContains, boundsOf, distance, metersPerDegreeLatitude, metersPerDegreeLongitude, polygonContains, polygonDistance } from './geo';

const HOUR = 3_600_000;
const DAY = 86_400_000;

// MARK: Regulatory state

const severity: Record<RegulatoryState, number> = {
  official: 0,
  noKnownRestriction: 1,
  unknown: 2,
  caution: 3,
  restricted: 4,
  prohibited: 5,
};

/** Higher = more restrictive; `unknown` outranks the permissive states so it can never be masked as "fine". */
export function regulatorySeverity(state: RegulatoryState): number {
  return severity[state];
}

export function isBlockingState(state: RegulatoryState): boolean {
  return state === 'restricted' || state === 'prohibited';
}

/** The most restrictive state wins; an empty list is `unknown`. */
export function combineRegulatoryStates(states: readonly RegulatoryState[]): RegulatoryState {
  let best: RegulatoryState | undefined;
  for (const s of states) if (best === undefined || severity[s] > severity[best]) best = s;
  return best ?? 'unknown';
}

export const jurisdictionSpecificity: Record<JurisdictionLevel, number> = {
  international: 0,
  federal: 1,
  cantonal: 2,
  municipal: 3,
  protectedAreaAuthority: 4,
  landowner: 5,
};

export function effectImpliedState(effect: RegulationEffect): RegulatoryState {
  switch (effect) {
    case 'prohibitsOvernight':
      return 'prohibited';
    case 'permitsOnlyAtDesignatedSites':
    case 'requiresPermission':
      return 'restricted';
    case 'toleratedWithConditions':
    case 'advisory':
      return 'caution';
    case 'noSpecificRule':
      return 'noKnownRestriction';
  }
}

export function overnightRuleImpliedState(rule: OvernightRule): RegulatoryState {
  switch (rule) {
    case 'prohibited':
      return 'prohibited';
    case 'restricted':
    case 'allowedAtDesignatedSites':
      return 'restricted';
    case 'noRuleKnown':
      return 'caution'; // being inside a protected area always warrants caution
    case 'unknown':
      return 'unknown';
  }
}

// MARK: Seasons and time validity

/** Month/day of an instant in UTC-independent local terms: the Swift code uses the Gregorian calendar of the device. */
function monthDay(date: EpochMs): number {
  const d = new Date(date);
  return (d.getMonth() + 1) * 100 + d.getDate();
}

/** Inclusive containment; windows may wrap the year end. */
export function seasonContains(window: SeasonalWindow, date: EpochMs): boolean {
  const value = monthDay(date);
  const start = window.startMonth * 100 + window.startDay;
  const end = window.endMonth * 100 + window.endDay;
  if (start <= end) return value >= start && value <= end;
  return value >= start || value <= end;
}

export const allYear: SeasonalWindow = { startMonth: 1, startDay: 1, endMonth: 12, endDay: 31 };

export function regulationIsInEffect(regulation: Regulation, date: EpochMs): boolean {
  const { effectiveFrom, effectiveUntil } = regulation.source;
  if (effectiveFrom !== undefined && date < effectiveFrom) return false;
  if (effectiveUntil !== undefined && date > effectiveUntil) return false;
  if (regulation.seasonalWindow) return seasonContains(regulation.seasonalWindow, date);
  return true;
}

export function coverageCovers(coverage: RegulatoryCoverage, c: GeoCoordinate): boolean {
  return coverage.polygons.some((p) => polygonContains(p, c));
}

// MARK: Protected areas

export function isWildlifeZone(kind: ProtectedAreaKind): boolean {
  return kind === 'wildlifeRestZone' || kind === 'wildlifeReserve';
}

export function protectedAreaBounds(area: ProtectedArea) {
  return boundsOf(area.polygons.flatMap((p) => p.exterior));
}

export function protectedAreaContains(area: ProtectedArea, c: GeoCoordinate): boolean {
  const b = protectedAreaBounds(area);
  if (b && !boundsContains(b, c)) return false;
  return area.polygons.some((p) => polygonContains(p, c));
}

export function protectedAreaIsActive(area: ProtectedArea, date: EpochMs): boolean {
  return area.seasonalWindow ? seasonContains(area.seasonalWindow, date) : true;
}

export function protectedAreaDistance(area: ProtectedArea, c: GeoCoordinate): number {
  let best = Infinity;
  for (const p of area.polygons) best = Math.min(best, polygonDistance(p, c));
  return best;
}

// MARK: Confidence, freshness

const confidenceRank: Record<Confidence, number> = { low: 0, medium: 1, high: 2 };

export function compareConfidence(a: Confidence, b: Confidence): number {
  return confidenceRank[a] - confidenceRank[b];
}

export function minConfidence(...values: Confidence[]): Confidence {
  return values.reduce((a, b) => (confidenceRank[b] < confidenceRank[a] ? b : a), 'high' as Confidence);
}

export const FreshnessPolicies = {
  weather: { freshFor: 3 * HOUR, agingAfter: 12 * HOUR, staleAfter: 48 * HOUR } satisfies FreshnessPolicy,
  regulations: { freshFor: 30 * DAY, agingAfter: 90 * DAY, staleAfter: 365 * DAY } satisfies FreshnessPolicy,
  maps: { freshFor: 180 * DAY, agingAfter: 365 * DAY, staleAfter: 730 * DAY } satisfies FreshnessPolicy,
  pointsOfInterest: { freshFor: 60 * DAY, agingAfter: 180 * DAY, staleAfter: 540 * DAY } satisfies FreshnessPolicy,
};

const freshnessRank: Record<DataFreshness, number> = { fresh: 0, aging: 1, stale: 2, expired: 3 };

export function compareFreshness(a: DataFreshness, b: DataFreshness): number {
  return freshnessRank[a] - freshnessRank[b];
}

export function evaluateFreshness(updatedAt: EpochMs, now: EpochMs, policy: FreshnessPolicy): DataFreshness {
  const age = now - updatedAt;
  if (age < 0) return 'fresh'; // clock skew: callers still show the timestamp
  if (age <= policy.freshFor) return 'fresh';
  if (age <= policy.agingAfter) return 'aging';
  if (age <= policy.staleAfter) return 'stale';
  return 'expired';
}

// MARK: Points of interest

export function poiCategoryGroup(category: POICategory): POICategoryGroup {
  switch (category) {
    case 'camping':
    case 'bivouacHut':
    case 'mountainHut':
    case 'shelter':
    case 'hotelHostel':
      return 'overnight';
    case 'village':
      return 'settlement';
    case 'water':
      return 'water';
    case 'foodShop':
    case 'restaurant':
      return 'food';
    case 'pharmacy':
    case 'doctor':
      return 'health';
    case 'train':
    case 'bus':
    case 'cableCar':
      return 'transit';
    case 'emergency':
      return 'emergency';
    case 'toilet':
    case 'laundry':
    case 'equipmentShop':
    case 'charging':
      return 'services';
    case 'roadAccess':
    case 'parking':
      return 'access';
  }
}

export const allPOICategories: readonly POICategory[] = [
  'camping',
  'bivouacHut',
  'mountainHut',
  'shelter',
  'hotelHostel',
  'village',
  'water',
  'foodShop',
  'restaurant',
  'pharmacy',
  'doctor',
  'train',
  'bus',
  'cableCar',
  'emergency',
  'toilet',
  'laundry',
  'equipmentShop',
  'charging',
  'roadAccess',
  'parking',
];

export function isOvernightCategory(category: POICategory): boolean {
  return poiCategoryGroup(category) === 'overnight';
}

/** Sleep Tonight category of a POI that can host an overnight stay. */
export function overnightCategoryOf(category: POICategory): OvernightCategory | undefined {
  switch (category) {
    case 'camping':
      return 'officialCampsite';
    case 'mountainHut':
      return 'mountainHut';
    case 'bivouacHut':
    case 'shelter':
      return 'shelter';
    case 'hotelHostel':
      return 'villageAccommodation';
    default:
      return undefined;
  }
}

export const allOvernightCategories: readonly OvernightCategory[] = [
  'officialCampsite',
  'mountainHut',
  'shelter',
  'villageAccommodation',
  'bivouacSearchArea',
];

export function preferredCategories(preference: OvernightPreference): OvernightCategory[] {
  switch (preference) {
    case 'tent':
      return ['bivouacSearchArea', 'officialCampsite', 'shelter'];
    case 'hut':
      return ['mountainHut', 'villageAccommodation', 'shelter'];
    case 'campsite':
      return ['officialCampsite', 'bivouacSearchArea'];
    case 'mixed':
      return [...allOvernightCategories];
  }
}

export function physicalSuitabilityFromScore(score: number): PhysicalSuitability {
  if (score >= 0.7) return 'high';
  if (score >= 0.45) return 'medium';
  return 'poor';
}

// MARK: Weather

const thunderRank: Record<ThunderstormRisk, number> = { none: 0, unknown: 1, low: 2, moderate: 3, high: 4 };

export function compareThunderstormRisk(a: ThunderstormRisk, b: ThunderstormRisk): number {
  return thunderRank[a] - thunderRank[b];
}

/** Hourly snapshot nearest to `date`, within 90 minutes. */
export function forecastSnapshotAt(forecast: WeatherForecast, date: EpochMs): WeatherSnapshot | undefined {
  let best: WeatherSnapshot | undefined;
  for (const s of forecast.hourly) {
    if (!best || Math.abs(s.time - date) < Math.abs(best.time - date)) best = s;
  }
  return best && Math.abs(best.time - date) <= 90 * 60_000 ? best : undefined;
}

export function forecastFreshness(forecast: WeatherForecast, now: EpochMs, policy = FreshnessPolicies.weather): DataFreshness {
  return evaluateFreshness(forecast.fetchedAt, now, policy);
}

// MARK: User reports

export function reportDefaultLifetime(kind: UserReportKind): number {
  switch (kind) {
    case 'waterFlowing':
    case 'waterDry':
      return 7 * DAY;
    case 'snow':
      return 5 * DAY;
    case 'trailBlocked':
    case 'damagedBridge':
      return 21 * DAY;
    case 'campsiteOpen':
    case 'campsiteClosed':
    case 'shelterAccessible':
    case 'shopClosed':
      return 30 * DAY;
    case 'regulationSignObserved':
      return 180 * DAY;
  }
}

export function reportRequiresLocationFuzzing(kind: UserReportKind): boolean {
  return kind === 'waterFlowing' || kind === 'waterDry' || kind === 'shelterAccessible' || kind === 'regulationSignObserved';
}

export function reportIsExpired(report: UserReport, date: EpochMs): boolean {
  return date >= report.expiresAt;
}

/** Snaps a coordinate to the centre of a grid cell (default 500 m) so a shared report does not reveal a position. */
export function fuzzLocation(c: GeoCoordinate, cellMeters = 500): GeoCoordinate {
  const latStep = cellMeters / metersPerDegreeLatitude;
  const lat = (Math.floor(c.latitude / latStep) + 0.5) * latStep;
  const lonStep = cellMeters / Math.max(1, metersPerDegreeLongitude(lat));
  const lon = (Math.floor(c.longitude / lonStep) + 0.5) * lonStep;
  return { latitude: lat, longitude: lon };
}

// MARK: Short bivouac trips (CoreModels/Trail/BivouacTrip.swift)

export const bivouacAreaMinimumRadius = 200;

export function effectiveAreaRadius(area: BivouacArea): number {
  return Number.isFinite(area.radiusMeters) ? Math.max(bivouacAreaMinimumRadius, area.radiusMeters) : bivouacAreaMinimumRadius;
}

export function bivouacAreaContains(area: BivouacArea, c: GeoCoordinate): boolean {
  return distance(area.centre, c) <= effectiveAreaRadius(area);
}

const exclusionLayerKeywords = [
  'jagdbanngebiet',
  'hochmoor',
  'flachmoor',
  'moorlandschaft',
  'auen',
  'nationalpark',
  'naturschutzgebiet',
  'nature-reserve',
  'nature_reserve',
  'naturereserve',
  'reserve',
];

const informationalLayerIDs = new Set([
  'ch.bafu.bundesinventare-bln',
  'ch.bafu.schutzgebiete-paerke_nationaler_bedeutung',
  'ch.bafu.unesco-weltnaturerbe',
  'ch.bafu.schutzgebiete-smaragd',
  'ch.bafu.bundesinventare-trockenwiesen_trockenweiden',
  'ch.bafu.waldreservate',
  'ch.bafu.gefahren-waldbrand_praeventionsmassnahmen_kantone',
  'geodienste.npl.overlays',
]);

export function isExclusionLayer(check: OfficialLayerCheck): boolean {
  const id = check.layer.toLowerCase();
  return exclusionLayerKeywords.some((k) => id.includes(k));
}

export function isInformationalLayer(check: OfficialLayerCheck): boolean {
  if (isExclusionLayer(check)) return false;
  const id = check.layer.toLowerCase();
  if (informationalLayerIDs.has(id)) return true;
  return check.title.toLowerCase().includes('(information only)');
}

export function ruleNoteSaysRulesUnknown(note: LocalRuleNote): boolean {
  const text = note.summary.toLowerCase();
  return text.includes('rules unknown') || text.includes('rules for a single night unknown') || text.includes('rules are unknown');
}

/** A trip's rules state is shown at most as "no known restriction" — never "official". */
export function tripDisplayRulesState(trip: BivouacTripInfo): RegulatoryState {
  return trip.rulesState === 'official' ? 'noKnownRestriction' : trip.rulesState;
}

export function tripDisplayRulesConfidence(trip: BivouacTripInfo): Confidence {
  return minConfidence(trip.rulesConfidence ?? 'low', 'medium');
}

export function tripExclusionHits(trip: BivouacTripInfo): OfficialLayerCheck[] {
  return trip.checks.filter((c) => c.result === 'inside' && isExclusionLayer(c));
}

/** Local rule notes that record the rules as unknown ("Rules unknown — ask …"). Swift `unknownRuleNotes`. */
export function tripUnknownRuleNotes(trip: BivouacTripInfo): LocalRuleNote[] {
  return trip.localRules.filter(ruleNoteSaysRulesUnknown);
}

/** Every free text of the trip (for wording checks). Swift `BivouacTripInfo.texts`. */
export function tripTexts(trip: BivouacTripInfo): string[] {
  const all: string[] = [];
  for (const text of [trip.water, trip.fallback, trip.access.start.seasonal, trip.access.end.seasonal]) {
    if (text !== undefined) all.push(text);
  }
  all.push(...trip.caveats);
  all.push(...trip.localRules.map((n) => n.summary));
  for (const check of trip.checks) if (check.detail !== undefined) all.push(check.detail);
  return all;
}

// MARK: Honest wording (CoreModels/Trail/BivouacTrip.swift `HonestWording`)

/** Words that must not be used affirmatively: TrailBivvy never states that a place is legal, allowed or safe. */
export const honestWordingForbiddenWords: ReadonlySet<string> = new Set([
  'legal',
  'legally',
  'lawful',
  'allowed',
  'allow',
  'allows',
  'permitted',
  'safe',
  'safely',
]);

const honestWordingNegations = new Set(['not', 'never', 'no', 'nor', "isn't", "aren't", "wasn't", "weren't", 'isn’t', 'aren’t']);

/** Affirmative uses of forbidden words in `text` (lower-cased words, in order); negated uses ("not allowed") pass. */
export function honestWordingViolations(text: string): string[] {
  const words = text.toLowerCase().match(/[\p{L}\p{M}'’]+/gu) ?? [];
  const found: string[] = [];
  words.forEach((word, i) => {
    if (!honestWordingForbiddenWords.has(word)) return;
    const previous = words.slice(Math.max(0, i - 2), i);
    if (previous.some((w) => honestWordingNegations.has(w) || w.endsWith("n't") || w.endsWith('n’t'))) return;
    found.push(word);
  });
  return found;
}

// MARK: Offline levels

const dataContents: OfflineContentKind[] = [
  'routeGeometry',
  'stages',
  'elevation',
  'pointsOfInterest',
  'protectedAreas',
  'wildlifeZones',
  'regulations',
  'transit',
  'terrain',
];

export const offlineLevels: readonly OfflinePackageLevel[] = ['routeOnly', 'standardMaps', 'detailedMaps', 'allData'];

export function offlineLevelContents(level: OfflinePackageLevel): Set<OfflineContentKind> {
  switch (level) {
    case 'routeOnly':
      return new Set(dataContents);
    case 'standardMaps':
      return new Set([...dataContents, 'baseMapStandard', 'hikingOverlay']);
    case 'detailedMaps':
      return new Set([...dataContents, 'baseMapStandard', 'baseMapDetailed', 'hikingOverlay']);
    case 'allData':
      return new Set([...dataContents, 'baseMapStandard', 'baseMapDetailed', 'hikingOverlay', 'weather']);
  }
}

export function offlineLevelZoomRange(level: OfflinePackageLevel): { min: number; max: number } | undefined {
  switch (level) {
    case 'routeOnly':
      return undefined;
    case 'standardMaps':
      return { min: 8, max: 14 };
    case 'detailedMaps':
    case 'allData':
      return { min: 8, max: 16 };
  }
}

export function offlineCorridorHalfWidth(level: OfflinePackageLevel): number {
  switch (level) {
    case 'routeOnly':
      return 2_000;
    case 'standardMaps':
      return 2_500;
    case 'detailedMaps':
    case 'allData':
      return 3_000;
  }
}

export function offlineRegionProgress(region: OfflineRegion): number {
  if (region.totalTiles > 0) return Math.min(1, region.completedTiles / region.totalTiles);
  return region.status === 'completed' ? 1 : 0;
}

// MARK: Provenance

export function syntheticDemoProvenance(dataset: string, generatedAt: EpochMs): DataProvenance {
  return {
    provider: 'TrailBivvy (synthetic demo data)',
    dataset,
    license: 'Synthetic sample data © TrailBivvy — not for navigation',
    fetchedAt: generatedAt,
    sourceUpdatedAt: generatedAt,
    isSynthetic: true,
    licenseReviewStatus: 'syntheticNoLicenceNeeded',
  };
}

export function userImportProvenance(fileName: string, importedAt: EpochMs): DataProvenance {
  return {
    provider: 'User import',
    dataset: fileName,
    license: 'Provided by the user — stays on device',
    fetchedAt: importedAt,
    isSynthetic: false,
    licenseReviewStatus: 'userProvided',
  };
}
